import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'

import {
  getVisibleInPersonPaymentMethods,
  labelize,
} from '../../../utilities/helpers'
import { markAsPaid } from '../../../utilities/api'

import { constants } from '@blocktickets/shared-utils'

import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'

import { DateInputWrapper } from '../../DateInputWrapper'
import { Spinner } from '../../LoadingContainer/Spinner'
import { Success } from '../Success'
import { Error } from '../Error'

const { PAYMENT_TYPE } = constants

const CASH_OR_CHECK = ['CASH', 'CHECK']

const toCalendarDate = (value) => {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export default function MarkAsPaid({
  order,
  reloadAssignments,
  cashOrCheckOnly = false,
}) {
  const { openModal, closeModal } = useModal()

  const navigate = useNavigate()

  // Store the PAYMENT_TYPE **key** (e.g. "CASH") since the <select> options use keys.
  // Backend expects keys as well (it maps via PAYMENT_TYPE[paymentMethod]).
  const [paymentMethod, setPaymentMethod] = useState('')

  const [paidDate, setPaidDate] = useState(new Date())

  const [isSaving, setIsSaving] = useState(false)

  const visibleInPersonPaymentMethods = cashOrCheckOnly
    ? CASH_OR_CHECK
    : getVisibleInPersonPaymentMethods(order?.organizationUUID)

  // set default payment method to order payment method
  useEffect(() => {
    if (order) {
      const raw = order?.paymentMethodType
      const rawStr = raw == null ? '' : String(raw)
      const normalizedKey = rawStr.toUpperCase().replace(/-/g, '_')

      const fallbackMethod = cashOrCheckOnly
        ? 'CASH'
        : visibleInPersonPaymentMethods?.[0] || ''
      const allowed = new Set(visibleInPersonPaymentMethods || [])

      // If order.paymentMethodType is already a PAYMENT_TYPE key (or close), use it.
      if (PAYMENT_TYPE[normalizedKey] && allowed.has(normalizedKey)) {
        setPaymentMethod(normalizedKey)
        return
      }

      // Otherwise, if it's a PAYMENT_TYPE value (e.g. "cash"), map back to its key ("CASH").
      const valueLower = rawStr.toLowerCase().replace(/-/g, '_')
      const matchingKey = Object.keys(PAYMENT_TYPE).find(
        (k) => PAYMENT_TYPE[k] === valueLower
      )
      if (matchingKey && allowed.has(matchingKey)) {
        setPaymentMethod(matchingKey)
        return
      }

      // Invoice orders only offer cash or check, so do not keep "invoice".
      setPaymentMethod(fallbackMethod)
    }
  }, [
    cashOrCheckOnly,
    order?.id,
    order?.paymentMethodType,
    visibleInPersonPaymentMethods,
  ])

  const handleClose = () => closeModal()

  // update order due date and payment type
  const handleSave = () => {
    setIsSaving(true)
    const data = {
      paidDate: toCalendarDate(paidDate),
      paymentMethod,
      orderId: order?.id,
    }
    markAsPaid(data)
      .then(() => {
        handleClose()
        setIsSaving(false)
        if (!isSaving) showStatusChangeSuccess()
      })
      .catch((err) => {
        setIsSaving(false)
        console.error('Error marking order as paid:', err)
        const serverMessage =
          err?.response?.data?.error?.message || err?.response?.data?.message
        if (!isSaving)
          openError(
            'Something went wrong!',
            serverMessage || 'Could not update order status.'
          )
      })
  }

  const openError = (title, description) => {
    openModal(
      Error,
      {
        title,
        description,
        onClose: closeModal,
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: true }
    )
  }

  const showStatusChangeSuccess = () => {
    openSuccess(
      cashOrCheckOnly ? 'Order finalized' : 'Payment marked as Paid!',
      cashOrCheckOnly
        ? 'This order is now paid by cash or check, and the invoice link is closed.'
        : 'The new status should now reflect accordingly for this order.'
    )
  }

  const openSuccess = (title, description) => {
    const eventRedirectURL = `/event/${order?.event?.uuid}/assign-tickets`
    const packageRedirectURL = `/package/${order?.package?.uuid}/assign-packages`
    const redirectURL = order?.event ? eventRedirectURL : packageRedirectURL

    openModal(
      Success,
      {
        title,
        description,
        highlightText: undefined,
        onClose: () => onSuccessClose(redirectURL),
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: true }
    )
  }

  const onSuccessClose = (redirectURL) => {
    closeModal()
    navigate(redirectURL)
    reloadAssignments()
  }

  return (
    <Modal.Body>
      <div className="modal-body-heading">
        <h1 className="modal-body-heading-title">
          {cashOrCheckOnly ? 'Pay by cash or check?' : 'Payment received?'}
        </h1>
        <p className="mt-0">
          {cashOrCheckOnly
            ? 'The invoice link was already sent. Recording cash or check closes that link so it cannot also be paid by card, and finalizes this order for the current invoice total.'
            : 'Add the payment details to keep records accurate.'}
        </p>
      </div>
      <Stack direction="horizontal" gap={4}>
        <Form.Group className="w-40" controlId="date">
          <Form.Label>Paid on</Form.Label>
          <DateInputWrapper
            id="paid-date"
            setDate={setPaidDate}
            selectedDate={paidDate}
            size="sm"
          />
        </Form.Group>
        <Form.Group className="flex-grow-1" controlId="method">
          <Form.Label>Payment method</Form.Label>
          <Form.Select
            name="method"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            autoFocus
          >
            {visibleInPersonPaymentMethods?.map((option, i) => (
              <option value={option} key={i}>
                {labelize(option)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Stack>
      <Stack direction="horizontal" className="btn-group-flex">
        <Button size="lg" variant="outline-light" onClick={handleClose}>
          Close
        </Button>
        <Button
          size="lg"
          className="btn-width btn-width-xl"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <Spinner />
          ) : cashOrCheckOnly ? (
            'Finalize order'
          ) : (
            'Update payment'
          )}
        </Button>
      </Stack>
    </Modal.Body>
  )
}
